import React, { useEffect, useState, useRef } from 'react'

import classNames from 'classnames'
import PropTypes from 'prop-types'
import { Transition } from 'react-transition-group'

import styling from './modal.module.scss'

/**
 * A dialog overlay
 */
const RobitModal = ({
  animation,
  backdrop = true,
  backdropClassName,
  className,
  children,
  closeOnBackdropClick = true,
  centered,
  footer,
  header,
  id,
  modalClassName,
  modalContentClassName,
  modalShown,
  modalHidden,
  open = false,
  position,
  role = 'dialog',
  size,
  toggleModal,
  tabIndex,
  withCloseButton,
  ...rest
}) => {
  const [isOpen, setOpenStatus] = useState(open || false)
  const modalContent = useRef(null)

  useEffect(() => {
    setOpenStatus(open)
  }, [open])

  const handleOnEntered = (type, node) => {
    node.classList.add('show')

    if (type === 'modal') {
      const modalContentClassesArray = contentClasses.split(' ')
      const modalContentQueryString = '.' + modalContentClassesArray.join('.')
      const modalContentDom = node.querySelector(modalContentQueryString)

      modalContentDom.focus()

      modalShown && modalShown()
    }
  }

  const handleOnExit = (type, node) => {
    node.classList.remove('show')
  }

  const handleOnExited = () => {
    modalHidden && modalHidden()
  }

  const handleBackdropClick = e => {
    if (!modalContent.current.contains(e.target) && closeOnBackdropClick) {
      toggleModal()
    }
  }

  if (!isOpen) {
    return false
  }

  const backdropClasses = classNames(styling['modal-backdrop'], 'fade', backdropClassName)

  const modalClasses = classNames(styling.modal, 'fade', modalClassName)

  const modalAttrs = {
    'aria-hidden': true,
    id: id || undefined,
    role,
    style: {
      display: 'block',
    },
    tabIndex,
  }

  const modalDialogClasses = classNames(
    styling['modal-dialog'],
    className,
    size && styling[`modal-${size}`],
    centered && styling['modal-dialog-centered'],
    position && styling[`modal-${position}`]
  )

  const contentClasses = classNames(styling['modal-content'], modalContentClassName)

  return (
    <>
      {backdrop && (
        <Transition
          appear={isOpen}
          in={isOpen}
          onEntered={node => handleOnEntered('backdrop', node)}
          onExit={node => handleOnExit('backdrop', node)}
          onExited={handleOnExited}
          timeout={150}
          mountOnEnter
          unmountOnExit
        >
          <div className={backdropClasses} />
        </Transition>
      )}
      <Transition
        appear={isOpen}
        in={isOpen}
        onClick={handleBackdropClick}
        onEntered={node => handleOnEntered('modal', node)}
        onExit={node => handleOnExit('modal', node)}
        timeout={150}
        mountOnEnter
        unmountOnExit
      >
        <div className={modalClasses} {...modalAttrs}>
          <div className={modalDialogClasses} role='document'>
            <div className={contentClasses} ref={modalContent} tabIndex='-1'>
              {children}
            </div>
          </div>
        </div>
      </Transition>
    </>
  )
}

RobitModal.propTypes = {
  /**
   */
  animation: PropTypes.bool,
  /**
   * Whether it should display a backdrop, or not. Defaults to true.
   */
  backdrop: PropTypes.bool,
  /**
   * The class name for the backdrop element.
   */
  backdropClassName: PropTypes.string,
  /**
   * Whether it should be centered, or not.
   */
  centered: PropTypes.bool,
  /**
   * The children nodes.
   */
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  /**
   * The class name.
   */
  className: PropTypes.string,
  /**
   * Whether or not clicking the backdrop closes the modal. Defaults to true.
   */
  closeOnBackdropClick: PropTypes.bool,
  /**
   * Optional footer content to display at the the bottom of the modal, sectioned out separately
   */
  footer: PropTypes.any,
  /**
   * Optional header content to display at the top of the modal, sectioned out separately
   */
  header: PropTypes.any,
  /**
   * The unique id of the component
   */
  id: PropTypes.string,
  /**
   * The class name for the modal.
   */
  modalClassName: PropTypes.string,
  /**
   * The class name for the modal content.
   */
  modalContentClassName: PropTypes.string,
  /**
   * The function that should be triggered when the modal is finally hidden.
   */
  modalHidden: PropTypes.func,
  /**
   * The function that should be triggered when the modal is shown.
   */
  modalShown: PropTypes.func,
  /**
   * Whether it is open, or not. Defaults to false
   */
  open: PropTypes.bool,
  /**
   * The position of the modal
   */
  position: PropTypes.string,
  /**
   * The role attribute for the modal. Defaults to 'dialog'
   */
  role: PropTypes.string,
  /**
   * The size. 'sm' or 'lg' (otherwise defaults to normal 'md')
   */
  size: PropTypes.string,
  /**
   * The tab index.
   */
  tabIndex: PropTypes.string,
  /**
   * The toggle function.
   */
  toggleModal: PropTypes.func,
  /**
   * Whether or not a "X" icon is included in the top right to allow the user to exit the dialog
   */
  withCloseButton: PropTypes.bool,
}

export default RobitModal
